/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteForm component
 */

import { Box, type BoxProps } from '@chakra-ui/react'
import { isZumper } from '@zcode/env'
import { buildStaticImageUrl } from '@zcode/nr-utils'
import React from 'react'
import type * as Api from '../../../api'
import { cn } from '../../../utils/css'
import searchGray from '../../svg/search.svg'
import { AutocompleteSubmitButton } from '../AutocompleteSubmitButton/AutocompleteSubmitButton'
import type { AutocompleteOrigin, AutocompleteTheme } from '../constants'
import { AutocompleteClearButton } from './AutocompleteClearButton'
import { AutocompleteInput } from './AutocompleteInput'

export interface AutocompleteFormProps {
  ariaDescribedby?: string
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  fillInputOnSelect?: boolean
  ghostTextProps?: BoxProps
  handleOnSearchBarClick?: () => void
  handleSearchButtonClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
    hoveredIndex: number,
    searchTerm: string
  ) => void
  hasError?: boolean
  hoveredIndex: number
  inputValueProps?: BoxProps
  isCurrentLocationLoading: boolean
  isMapListNav?: boolean
  isMobile: boolean
  onFillInput: (e: React.FormEvent) => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onManualSearch: (e: React.FormEvent) => void
  origin?: AutocompleteOrigin
  placeholder?: string
  predictionResult: string | null
  resetForm: () => void
  searchTerm: string
  setFieldValue: (field: 'searchTerm', value: string, validate?: boolean | undefined) => void
  shouldFocusAutoComplete?: boolean
  showMobileAutocomplete?: boolean
  theme: AutocompleteTheme
}

export const AutocompleteForm = ({
  ariaDescribedby,
  exclude,
  fillInputOnSelect,
  handleOnSearchBarClick,
  handleSearchButtonClick,
  hasError,
  hoveredIndex,
  isCurrentLocationLoading,
  isMapListNav,
  isMobile,
  onInputChange,
  onFillInput,
  onManualSearch,
  origin,
  placeholder,
  resetForm,
  searchTerm,
  setFieldValue,
  shouldFocusAutoComplete,
  showMobileAutocomplete,
  theme,
}: AutocompleteFormProps) => {
  return isZumper ? (
    <Box<'form'>
      as="form"
      backgroundColor="gray.0"
      className={cn(theme.searchForm, showMobileAutocomplete && theme.searchFormModal)}
      onClick={handleOnSearchBarClick}
      onSubmit={fillInputOnSelect ? onFillInput : onManualSearch}
      role="presentation"
      sx={{
        'input[type=search]': {
          backgroundColor: 'transparent',
          caretColor: 'black',
          fontFamily: 'body',
          zIndex: { base: 4, md: 5 },
          '&:focus': {
            zIndex: { base: 4, md: 5 },
          },
        },
        button: {
          zIndex: 5,
        },
      }}
    >
      {isCurrentLocationLoading && !isMobile ? (
        <i
          style={{
            backgroundImage: `url(${buildStaticImageUrl({
              url: 'img/shared/spinner/new_spinner.png',
              fit: 'clip',
            })})`,
          }}
          className={cn(
            'inline-block',
            'w-3',
            'h-3',
            'bg-no-repeat',
            'animate-spin',
            'bg-contain',
            theme.spinnerIcon
          )}
        />
      ) : (
        !showMobileAutocomplete && (
          <img alt="" className={theme.searchIcon} loading="eager" src={searchGray} />
        )
      )}
      <AutocompleteInput
        ariaDescribedby={ariaDescribedby}
        exclude={exclude}
        onChange={onInputChange}
        origin={origin}
        placeholder={placeholder}
        shouldFocusAutoComplete={shouldFocusAutoComplete}
        showMobileAutocomplete={showMobileAutocomplete}
        theme={theme}
      />
      {/* Commented out until we revisit adding ghost text again */}
      {/* {searchTerm && (
        <>
          <Box<'div'>
            backgroundColor="gray.0"
            color="gray.0"
            fontFamily="body"
            left={0}
            lineHeight="24px"
            maxWidth="calc(100% - 48px)"
            paddingLeft={{ base: 0, md: '46px' }}
            paddingY="20px"
            position="absolute"
            top={0}
            zIndex="3"
            {...inputValueProps}
          >
            {searchTerm}
          </Box>
          <Box<'div'>
            color="gray.300"
            fontFamily="body"
            left="0"
            lineHeight="24px"
            maxWidth="calc(100% - 48px)"
            overflow="hidden"
            paddingLeft={{ base: 0, md: '46px' }}
            paddingY="20px"
            position="absolute"
            textOverflow="ellipsis"
            top="0"
            whiteSpace="nowrap"
            {...ghostTextProps}
          >
            {predictionResult}
          </Box>
        </>
      )} */}
      {searchTerm && (
        <AutocompleteClearButton
          isMapListNav={isMapListNav}
          resetForm={resetForm}
          setFieldValue={setFieldValue}
          theme={theme}
        />
      )}
      {handleSearchButtonClick && (
        <AutocompleteSubmitButton
          hoveredIndex={hoveredIndex}
          onSubmit={handleSearchButtonClick}
          searchTerm={searchTerm}
        />
      )}
    </Box>
  ) : (
    <form
      className={cn(theme.searchForm, showMobileAutocomplete && theme.searchFormModal)}
      onSubmit={fillInputOnSelect ? onFillInput : onManualSearch}
      role="presentation"
    >
      {isCurrentLocationLoading ? (
        <i
          style={{
            backgroundImage: `url(${buildStaticImageUrl({
              url: 'img/shared/spinner/new_spinner.png',
              fit: 'clip',
            })})`,
          }}
          className={cn(
            'inline-block',
            'w-3',
            'h-3',
            'bg-no-repeat',
            'animate-spin',
            'bg-contain',
            theme.spinnerIcon
          )}
        />
      ) : (
        <img alt="" className={theme.searchIcon} loading="eager" src={searchGray} />
      )}
      <AutocompleteInput
        ariaDescribedby={ariaDescribedby}
        exclude={exclude}
        onChange={onInputChange}
        origin={origin}
        placeholder={placeholder}
        showMobileAutocomplete={showMobileAutocomplete}
        theme={theme}
      />
      {searchTerm && !hasError && <AutocompleteClearButton resetForm={resetForm} theme={theme} />}
    </form>
  )
}
